import React, { useEffect } from 'react';
import { Redirect, Stack, usePathname, useSegments } from 'expo-router';
import { View, StyleSheet, Linking, ActivityIndicator, Platform, Text, TouchableOpacity } from 'react-native';
import { observer } from '@legendapp/state/react';
import AppInitializer from '../components/AppInitializer';
import { globalState } from '../state/globalState';
import theme from '../config/theme';
import { AppLayoutLogger } from '../utils/logger';
import { ActionSheetProvider } from '@expo/react-native-action-sheet';
import { WebAlertProvider } from '../components/WebAlert';
import PdfDownloadPromptListener from '../components/PdfDownloadPromptListener';
import { setupAuthDeepLinks } from '../services/authLinks';
import { debugPause } from '../utils/debugPause';
import { Toaster } from '../utils/toast';
import { InAppCameraHost } from '../components/media/InAppCameraHost';
import { APP_CONSTANTS } from '../config/constants';
import { IS_CHECKLIST_RECOVERY_DIAGNOSTIC } from '../config/recoveryDiagnostic';
import AdvancedScreen from './(app)/settings/advanced';
import {
  clearSentryUser,
  initializeSentry,
  setSentryUser,
} from '../services/monitoring/sentry';

initializeSentry();

// Layout principal que envolve toda a aplicação
const RootLayout = observer(() => {
  const isLoading = globalState.user.isLoading.get();
  const isAuthenticated = globalState.user.isAuthenticated.get();
  const existsInDatabase = globalState.user.existsInDatabase.get();
  const isPasswordRecovery = globalState.user.isPasswordRecovery.get();
  const checkUserDatabaseDone = globalState.user.checkUserDatabaseDone.get();
  const directSignupState = globalState.user.directSignup.get();
  const userId = globalState.user.user.get()?.id;
  const userEmail = globalState.user.user.get()?.email;
  const userCompanyId =
    globalState.user.profile.get()?.empresa_id ||
    globalState.company.data.get()?.id ||
    null;
  const pathname = usePathname();
  const rawSegments = useSegments();
  const segments = Array.isArray(rawSegments) ? rawSegments : [];
  const normalizedPathname = typeof pathname === 'string' ? pathname : '';
  // Fallback para web: em alguns ciclos iniciais o Expo Router ainda não resolve pathname/segments.
  const browserPathname =
    Platform.OS === 'web' && typeof window !== 'undefined' && typeof window.location?.pathname === 'string'
      ? window.location.pathname
      : '';
  const effectivePathname = normalizedPathname || browserPathname;
  const isInsideAppGroup = segments[0] === '(app)';
  const isInsideAuthGroup = segments[0] === '(auth)';
  const isInsidePublicPath =
    effectivePathname === '/public' || effectivePathname.startsWith('/public/');
  const isInsidePublicGroup = segments[0] === 'public' || isInsidePublicPath;
  const currentAuthRoute = isInsideAuthGroup && segments.length > 1 ? segments[1] : null;
  const registrationRoutes = ['register', 'register-user'];
  const isInRegistrationFlow =
    isInsideAuthGroup && currentAuthRoute ? registrationRoutes.includes(currentAuthRoute) : false;
  const publicAuthRoutes = ['login', 'signup', 'reset-password'];
  const isOnPublicAuthRoute =
    isInsideAuthGroup && currentAuthRoute ? publicAuthRoutes.includes(currentAuthRoute) : false;
  const isDirectSignUpFlow = !APP_CONSTANTS.REGISTRATION.USE_SIGNUP_REDIRECT_FLOW;
  const directSignupBlocking =
    isDirectSignUpFlow &&
    isAuthenticated &&
    (directSignupState.isBootstrapping || (!directSignupState.lastError && directSignupState.isRequired));

  useEffect(() => {
    if (directSignupBlocking) {
      AppLayoutLogger.Main.info(
        '[DIRECT-SIGNUP] Redirecionamento para telas protegidas bloqueado até bootstrap concluir'
      );
    } else if (isDirectSignUpFlow && isAuthenticated) {
      AppLayoutLogger.Main.info('[DIRECT-SIGNUP] Bootstrap concluído; liberando roteamento normal');
    }
  }, [directSignupBlocking, isDirectSignUpFlow, isAuthenticated]);

  useEffect(() => {
    if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
      return;
    }

    if (isAuthenticated && userId) {
      setSentryUser({
        id: userId,
        email: userEmail || null,
        empresaId: userCompanyId,
      });
      return;
    }

    clearSentryUser();
  }, [isAuthenticated, userCompanyId, userEmail, userId]);

  useEffect(() => {
    AppLayoutLogger.Init.info('Inicialização completa do layout principal');

    return () => {
      AppLayoutLogger.Main.info('Layout principal desmontado');
    };
  }, []);

  // Configurar deep links para autenticação
  useEffect(() => {
    if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
      return;
    }

    AppLayoutLogger.Main.info('Configurando deep links no RootLayout');

    // Configurar listener para URLs
    const cleanup = setupAuthDeepLinks();
    // Verificar URL inicial (caso o app tenha sido aberto por uma URL)
    const checkInitialURL = async () => {
      try {
        const initialURL = await Linking.getInitialURL();
        if (initialURL && !IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
          AppLayoutLogger.Main.info(`checkInitialURL: App aberto com URL inicial: ${initialURL}`);
        } else {
          AppLayoutLogger.Main.info('checkInitialURL: App aberto sem URL inicial');
        }
      } catch (error) {
        AppLayoutLogger.Main.error('checkInitialURL: Erro ao verificar URL inicial:', error);
      }
    };
    checkInitialURL();
    return () => {
      cleanup();
      AppLayoutLogger.Main.info('checkSetupDeepLinks: Limpeza de deep links no RootLayout');
    };
  }, []);

  // Verificação de existência do usuário foi centralizada no AppInitializer
  // para evitar corrida com a inicialização do banco de dados.

  // Função para redirecionar com log de timestamp
  const redirectToApp = () => {
    const timestamp = new Date().toISOString();
    if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
      const isRecoveryRoute =
        isInsideAppGroup && segments[1] === 'settings' && segments[2] === 'advanced';
      return isRecoveryRoute ? null : <Redirect href="/(app)/settings/advanced" />;
    }
    if (isInsideAppGroup || isInsidePublicGroup) {
      AppLayoutLogger.Main.info(
        `[${timestamp}] Usuário já está em rota permitida para sessão atual; mantendo rota. pathname=${pathname}, segments=${JSON.stringify(segments)}`
      );
      return null;
    }
    AppLayoutLogger.Main.info(`[${timestamp}] Redirecionando para área do app (/(app)`);
    return <Redirect href="/(app)" />;
  };

  // Função para redirecionar para registro com log de timestamp
  const redirectToRegister = () => {
    const timestamp = new Date().toISOString();
    if (isInRegistrationFlow) {
      AppLayoutLogger.Main.info(
        `[${timestamp}] Usuário já está no fluxo de registro (rota atual: ${currentAuthRoute}); mantendo rota atual. pathname=${pathname}, segments=${JSON.stringify(segments)}`
      );
      return null;
    }
    AppLayoutLogger.Main.info(`[${timestamp}] Redirecionando para tela de registro (/(auth)/register)`);
    return <Redirect href="/(auth)/register" />;
  };

  const ensureLoginRoute = () => {
    const timestamp = new Date().toISOString();
    if (isInsidePublicGroup && !IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
      AppLayoutLogger.Main.info(
        `[${timestamp}] Usuário anônimo em rota pública; mantendo rota atual. pathname=${pathname}, segments=${JSON.stringify(segments)}`
      );
      return null;
    }
    if (isInsideAuthGroup && isOnPublicAuthRoute) {
      AppLayoutLogger.Main.info(
        `[${timestamp}] Usuário não autenticado já está em rota pública do /(auth); mantendo tela atual. pathname=${pathname}, segments=${JSON.stringify(segments)}`
      );
      return null;
    }
    AppLayoutLogger.Main.info(
      `[${timestamp}] Usuário não autenticado em rota restrita (${currentAuthRoute ?? 'desconhecida'}); enviando para login. pathname=${normalizedPathname}, browserPathname=${browserPathname}, effectivePathname=${effectivePathname}, segments=${JSON.stringify(segments)}`
    );
    return <Redirect href="/(auth)/login" />;
  };

  AppLayoutLogger.Render.info(
    `Renderizando RootLayout [isLoading: ${isLoading}, isAuthenticated: ${isAuthenticated}, existsInDatabase: ${existsInDatabase}]`
  );

  const renderLoadingState = (message: string) => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size="large" color={theme.colors.primary.default} />
      <Text style={styles.loadingMessage}>{message}</Text>
    </View>
  );

  const handleReauth = async () => {
    AppLayoutLogger.Main.info('[AUTH-RECOVERY] Solicitando novo login por segurança');
    const { logout } = require('../services/authService') as typeof import('../services/authService');
    await logout();
  };

  const renderReauthState = () => (
    <View style={styles.reauthContainer}>
      <Text style={styles.reauthTitle}>Validação necessária</Text>
      <Text style={styles.reauthMessage}>Por segurança, faça o login novamente para concluir.</Text>
      <TouchableOpacity style={styles.reauthButton} onPress={handleReauth}>
        <Text style={styles.reauthButtonText}>Sair e fazer login</Text>
      </TouchableOpacity>
    </View>
  );

  if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
    return (
      <ActionSheetProvider>
        <AppInitializer>
          <View style={styles.container}>
            {isLoading || (isAuthenticated && !checkUserDatabaseDone) ? (
              <View style={styles.recoveryOverlay}>
                {renderLoadingState('Abrindo dados locais em modo seguro...')}
              </View>
            ) : isAuthenticated && existsInDatabase ? (
              <View style={styles.recoveryOverlay}>
                <AdvancedScreen />
              </View>
            ) : (
              <View style={[styles.reauthContainer, styles.recoveryOverlay]}>
                <Text style={styles.reauthTitle}>Sessão local indisponível</Text>
                <Text style={styles.reauthMessage}>
                  Não desinstale nem limpe os dados do aplicativo. Feche esta versão e contate o suporte para preservar o banco local.
                </Text>
              </View>
            )}
          </View>
        </AppInitializer>
      </ActionSheetProvider>
    );
  }

  return (
    <WebAlertProvider>
      <PdfDownloadPromptListener />
      <ActionSheetProvider>
        <AppInitializer>
          <View style={styles.container}>
            {isLoading ? (
              // Durante o carregamento, mostrar apenas o Stack para evitar redirecionamentos prematuros
              <Stack screenOptions={{ headerShown: false }} />
            ) : (
              // Após a inicialização, redirecionar baseado no estado
              <>
                <Stack screenOptions={{ headerShown: false }} />

                {isAuthenticated ? (
                  // Verificar se é modo de recuperação de senha (prioridade sobre outros estados)
                  isPasswordRecovery ? (
                    // Modo de recuperação de senha: ir para tela de reset independente do cadastro
                    <Redirect href="/(auth)/reset-password" />
                  ) : !checkUserDatabaseDone || directSignupBlocking ? (
                    // Aguardar inicialização/bootstrapping concluir antes de redirecionar
                    renderLoadingState(
                      directSignupBlocking
                        ? 'Estamos configurando sua conta. Aguarde alguns instantes...'
                        : 'Preparando seu ambiente...'
                    )
                  ) : existsInDatabase ? (
                    // Autenticado e existente: ir para o app
                    redirectToApp()
                  ) : (
                    // Autenticado mas não validado no banco: solicitar novo login
                    renderReauthState()
                  )
                ) : (
                  // Não autenticado: manter telas do grupo /(auth) ou enviar para login
                  ensureLoginRoute()
                )}
              </>
            )}
            <Toaster />
            <InAppCameraHost />
          </View>
        </AppInitializer>
      </ActionSheetProvider>
    </WebAlertProvider>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingMessage: {
    marginTop: theme.spacing.md,
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.md,
    textAlign: 'center',
    paddingHorizontal: theme.spacing.lg,
  },
  reauthContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.xl,
  },
  reauthTitle: {
    fontSize: theme.fontSizes.lg,
    fontWeight: theme.fontWeights.bold as any,
    color: theme.colors.text.primary,
    textAlign: 'center',
    marginBottom: theme.spacing.sm,
  },
  reauthMessage: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
    textAlign: 'center',
    marginBottom: theme.spacing.lg,
  },
  reauthButton: {
    backgroundColor: theme.colors.primary.default,
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.sm,
  },
  reauthButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
  },
  recoveryOverlay: {
    ...StyleSheet.absoluteFill,
    backgroundColor: theme.colors.background.secondary,
  },
});

export default RootLayout; 
